import React, { useState } from 'react';
import { CheckSquare, Plus, Square, Trash2 } from 'lucide-react';
import { useConstruction } from '../../context/ConstructionContext';
import { toIsoDate } from '../../construction/evm';
import DataTable from '../DataTable';
import Badge from '../common/Badge';
import Button from '../common/Button';
import ConstructionShell, { FormError, Panel, inputClass, labelClass, runAction, textareaClass } from './ConstructionShell';
import { t } from '../../i18n';

const NC_STATUS = {
  abierta: { label: 'Abierta', tone: 'danger' },
  en_tratamiento: { label: 'En tratamiento', tone: 'warning' },
  cerrada: { label: 'Cerrada', tone: 'success' }
};
const SEVERITY_TONE = { alta: 'danger', media: 'warning', baja: 'neutral' };

const CHECKLIST_TEMPLATES = {
  calidad: ['Trabajo ejecutado según planos vigentes', 'Materiales con certificado de calidad', 'Tolerancias dimensionales dentro de especificación', 'Registro fotográfico adjunto'],
  hse: ['Charla de 5 minutos realizada', 'EPP completo en cuadrillas', 'Permisos de trabajo vigentes', 'Zona de trabajo delimitada y señalizada']
};

const TABS = [
  { id: 'inspections', label: 'Inspecciones y checklists' },
  { id: 'ncs', label: 'No conformidades' },
  { id: 'commissioning', label: 'Puesta en marcha / comisionado' }
];

const QualityContent = ({ summary }) => {
  const { state, can, addInspection, addNonConformity, updateNonConformityStatus, getCommissioning, toggleCommissioningItem, userName } = useConstruction();
  const [tab, setTab] = useState('inspections');
  const [error, setError] = useState('');
  const [inspection, setInspection] = useState(null);
  const [ncForm, setNcForm] = useState(null);
  const nameById = Object.fromEntries(summary.items.map((item) => [item.id, `${item.code} ${item.name}`]));
  const itemOptions = summary.evm.rows;
  const inspections = state.inspections.filter((entry) => entry.parkId === summary.parkId);
  const ncs = state.nonConformities.filter((entry) => entry.parkId === summary.parkId);
  const commissioning = getCommissioning(summary.parkId);
  const doneCount = commissioning.filter((entry) => entry.done).length;
  const ready = doneCount === commissioning.length;

  const startInspection = (type = 'calidad') =>
    setInspection({ type, wbsItemId: '', date: toIsoDate(new Date()), notes: '', checklist: CHECKLIST_TEMPLATES[type].map((label) => ({ label, ok: null })) });

  const submitInspection = (event) => {
    event.preventDefault();
    const saved = runAction(() => addInspection({ ...inspection, parkId: summary.parkId, wbsItemId: inspection.wbsItemId || null }), setError);
    if (saved) {
      setInspection(null);
      if (saved.result === 'observado' && can('quality.edit')) {
        setNcForm({ title: '', description: saved.notes || '', severity: 'media', wbsItemId: saved.wbsItemId || '', responsibleName: '', dueDate: '', inspectionId: saved.id });
        setTab('ncs');
      }
    }
  };

  const submitNc = (event) => {
    event.preventDefault();
    const saved = runAction(() => addNonConformity({ ...ncForm, parkId: summary.parkId, wbsItemId: ncForm.wbsItemId || null }), setError);
    if (saved) setNcForm(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2">
        {TABS.map((entry) => (
          <Button key={entry.id} size="sm" variant={tab === entry.id ? 'primary' : 'secondary'} onClick={() => { setError(''); setTab(entry.id); }}>
            {t(entry.label)}
            {entry.id === 'ncs' && ncs.filter((nc) => nc.status !== 'cerrada').length > 0 ? ` (${ncs.filter((nc) => nc.status !== 'cerrada').length})` : ''}
          </Button>
        ))}
      </div>
      <FormError message={error} />

      {tab === 'inspections' && (
        <Panel
          title="Inspecciones"
          description="Checklists de calidad y HSE por partida. Un punto no conforme deja la inspección observada y propone abrir una NC."
          actions={can('quality.edit') && !inspection ? (
            <>
              <Button variant="primary" size="sm" icon={Plus} onClick={() => startInspection('calidad')}>{t('Inspección de calidad')}</Button>
              <Button size="sm" icon={Plus} onClick={() => startInspection('hse')}>{t('Inspección HSE')}</Button>
            </>
          ) : null}
        >
          {inspection && (
            <form onSubmit={submitInspection} className="mb-4 border border-neutral-200 rounded p-3 bg-neutral-50 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className={labelClass}>{t('Tipo')}</label>
                  <select className={inputClass} value={inspection.type} onChange={(event) => startInspection(event.target.value)}>
                    <option value="calidad">{t('Calidad')}</option>
                    <option value="hse">{t('HSE')}</option>
                  </select>
                </div>
                <div>
                  <label className={labelClass}>{t('Partida')}</label>
                  <select className={inputClass} value={inspection.wbsItemId} onChange={(event) => setInspection((prev) => ({ ...prev, wbsItemId: event.target.value }))}>
                    <option value="">{t('Sin partida asociada')}</option>
                    {itemOptions.map((row) => <option key={row.id} value={row.id}>{`${row.code} ${row.name}`}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelClass}>{t('Fecha')}</label>
                  <input type="date" className={inputClass} value={inspection.date} onChange={(event) => setInspection((prev) => ({ ...prev, date: event.target.value }))} />
                </div>
              </div>
              <div className="space-y-1.5">
                {inspection.checklist.map((entry, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <input className={inputClass} value={entry.label} onChange={(event) => setInspection((prev) => ({ ...prev, checklist: prev.checklist.map((row, i) => (i === index ? { ...row, label: event.target.value } : row)) }))} />
                    {[{ value: true, label: 'Conforme' }, { value: false, label: 'No conforme' }].map((option) => (
                      <button
                        key={String(option.value)}
                        type="button"
                        onClick={() => setInspection((prev) => ({ ...prev, checklist: prev.checklist.map((row, i) => (i === index ? { ...row, ok: option.value } : row)) }))}
                        className={`h-8 px-2.5 text-xs rounded border whitespace-nowrap ${entry.ok === option.value ? (option.value ? 'bg-success-50 border-success-100 text-success-700' : 'bg-danger-50 border-danger-100 text-danger-700') : 'bg-white border-neutral-300 text-neutral-600'}`}
                      >
                        {t(option.label)}
                      </button>
                    ))}
                    <button type="button" className="text-neutral-400 hover:text-danger-600" title={t('Quitar')} onClick={() => setInspection((prev) => ({ ...prev, checklist: prev.checklist.filter((_, i) => i !== index) }))}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <Button size="sm" icon={Plus} onClick={() => setInspection((prev) => ({ ...prev, checklist: [...prev.checklist, { label: '', ok: null }] }))}>{t('Agregar punto')}</Button>
              </div>
              <textarea rows={2} className={textareaClass} placeholder={t('Observaciones')} value={inspection.notes} onChange={(event) => setInspection((prev) => ({ ...prev, notes: event.target.value }))} />
              <div className="flex gap-2">
                <Button type="submit" variant="primary" size="sm">{t('Guardar inspección')}</Button>
                <Button size="sm" onClick={() => setInspection(null)}>{t('Cancelar')}</Button>
              </div>
            </form>
          )}
          <DataTable
            data={inspections.map((entry) => ({
              ...entry,
              typeLabel: entry.type === 'hse' ? 'HSE' : 'Calidad',
              itemName: nameById[entry.wbsItemId] || '—',
              score: `${entry.checklist.filter((row) => row.ok === true).length}/${entry.checklist.length}`,
              inspector: userName(entry.inspectorId)
            }))}
            columns={[
              { key: 'date', label: 'Fecha', sortable: true },
              { key: 'typeLabel', label: 'Tipo', render: (value) => t(value) },
              { key: 'itemName', label: 'Partida', sortable: true },
              { key: 'score', label: 'Conformes', align: 'right' },
              { key: 'result', label: 'Resultado', render: (value) => <Badge tone={value === 'conforme' ? 'success' : 'warning'}>{t(value === 'conforme' ? 'Conforme' : 'Observado')}</Badge> },
              { key: 'inspector', label: 'Inspector' },
              { key: 'notes', label: 'Observaciones', render: (value) => value || '—' }
            ]}
            searchPlaceholder="Buscar inspección..."
            emptyTitle="Sin inspecciones"
            emptyDescription="Registra inspecciones de calidad o HSE por partida."
          />
        </Panel>
      )}

      {tab === 'ncs' && (
        <Panel
          title="No conformidades"
          description="Apertura, responsable, plazo, tratamiento y cierre."
          actions={can('quality.edit') && !ncForm ? <Button variant="primary" size="sm" icon={Plus} onClick={() => setNcForm({ title: '', description: '', severity: 'media', wbsItemId: '', responsibleName: '', dueDate: '' })}>{t('Nueva NC')}</Button> : null}
        >
          {ncForm && (
            <form onSubmit={submitNc} className="mb-4 border border-neutral-200 rounded p-3 bg-neutral-50 grid grid-cols-1 md:grid-cols-4 gap-3">
              <div className="md:col-span-2">
                <label className={labelClass}>{t('Título')}</label>
                <input className={inputClass} value={ncForm.title} onChange={(event) => setNcForm((prev) => ({ ...prev, title: event.target.value }))} />
              </div>
              <div>
                <label className={labelClass}>{t('Severidad')}</label>
                <select className={inputClass} value={ncForm.severity} onChange={(event) => setNcForm((prev) => ({ ...prev, severity: event.target.value }))}>
                  <option value="alta">{t('Alta')}</option>
                  <option value="media">{t('Media')}</option>
                  <option value="baja">{t('Baja')}</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>{t('Plazo de cierre')}</label>
                <input type="date" className={inputClass} value={ncForm.dueDate} onChange={(event) => setNcForm((prev) => ({ ...prev, dueDate: event.target.value }))} />
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>{t('Partida')}</label>
                <select className={inputClass} value={ncForm.wbsItemId} onChange={(event) => setNcForm((prev) => ({ ...prev, wbsItemId: event.target.value }))}>
                  <option value="">{t('Sin partida asociada')}</option>
                  {itemOptions.map((row) => <option key={row.id} value={row.id}>{`${row.code} ${row.name}`}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className={labelClass}>{t('Responsable')}</label>
                <input className={inputClass} value={ncForm.responsibleName} onChange={(event) => setNcForm((prev) => ({ ...prev, responsibleName: event.target.value }))} />
              </div>
              <div className="md:col-span-4">
                <label className={labelClass}>{t('Descripción')}</label>
                <textarea rows={2} className={textareaClass} value={ncForm.description} onChange={(event) => setNcForm((prev) => ({ ...prev, description: event.target.value }))} />
              </div>
              <div className="md:col-span-4 flex gap-2">
                <Button type="submit" variant="primary" size="sm">{t('Abrir NC')}</Button>
                <Button size="sm" onClick={() => setNcForm(null)}>{t('Cancelar')}</Button>
              </div>
            </form>
          )}
          <DataTable
            data={ncs.map((nc) => ({ ...nc, itemName: nameById[nc.wbsItemId] || '—', statusLabel: NC_STATUS[nc.status]?.label }))}
            columns={[
              { key: 'title', label: 'No conformidad', sortable: true, render: (value, row) => <div><p className="font-medium text-neutral-900">{value}</p>{row.description && <p className="text-xs text-neutral-500">{row.description}</p>}</div> },
              { key: 'severity', label: 'Severidad', render: (value) => <Badge tone={SEVERITY_TONE[value]}>{t(value === 'alta' ? 'Alta' : value === 'baja' ? 'Baja' : 'Media')}</Badge> },
              { key: 'itemName', label: 'Partida' },
              { key: 'responsibleName', label: 'Responsable', render: (value) => value || '—' },
              { key: 'openedAt', label: 'Apertura', sortable: true },
              { key: 'dueDate', label: 'Plazo', sortable: true, render: (value) => value || '—' },
              { key: 'statusLabel', label: 'Estado', render: (value, row) => <Badge tone={NC_STATUS[row.status]?.tone}>{t(value)}</Badge> },
              {
                key: 'id',
                label: 'Acciones',
                render: (value, row) => {
                  if (row.status === 'cerrada') return <span className="text-xs text-neutral-500">{t('Cerrada el {0}', row.closedAt)}</span>;
                  return (
                    <div className="flex gap-1">
                      {row.status === 'abierta' && can('quality.edit') && (
                        <Button size="sm" onClick={() => runAction(() => updateNonConformityStatus(value, 'en_tratamiento'), setError)}>{t('En tratamiento')}</Button>
                      )}
                      {can('quality.closeNc') && (
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => {
                            const note = window.prompt(t('Describe el cierre de la no conformidad:'));
                            if (note !== null) runAction(() => updateNonConformityStatus(value, 'cerrada', note), setError);
                          }}
                        >
                          {t('Cerrar')}
                        </Button>
                      )}
                    </div>
                  );
                }
              }
            ]}
            searchPlaceholder="Buscar no conformidad..."
            emptyTitle="Sin no conformidades"
            emptyDescription="Las NC se abren desde una inspección observada o manualmente."
          />
        </Panel>
      )}

      {tab === 'commissioning' && (
        <Panel
          title="Checklist de puesta en marcha / comisionado"
          description="Puente hacia Operaciones: al completarse, la obra queda lista para traspaso."
          actions={<Badge tone={ready ? 'success' : 'warning'}>{ready ? t('Lista para traspaso a Operaciones') : t('{0} de {1} completados', doneCount, commissioning.length)}</Badge>}
        >
          <div className="h-2 bg-neutral-100 rounded mb-4 overflow-hidden">
            <div className="h-full bg-success-500" style={{ width: `${(doneCount / commissioning.length) * 100}%` }} />
          </div>
          <ul className="divide-y divide-neutral-100">
            {commissioning.map((entry) => (
              <li key={entry.id} className="flex items-center justify-between gap-3 py-2">
                <button
                  type="button"
                  disabled={!can('commissioning.edit')}
                  onClick={() => runAction(() => toggleCommissioningItem(summary.parkId, entry.id), setError)}
                  className="flex items-center gap-2 text-sm text-left text-neutral-800 disabled:cursor-default"
                >
                  {entry.done ? <CheckSquare className="w-4 h-4 text-success-600" /> : <Square className="w-4 h-4 text-neutral-400" />}
                  <span className={entry.done ? 'line-through text-neutral-500' : ''}>{t(entry.label)}</span>
                </button>
                {entry.done && <span className="text-xs text-neutral-500">{t('{0} · {1}', entry.doneAt, userName(entry.doneBy))}</span>}
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </div>
  );
};

const ConstructionQuality = () => (
  <ConstructionShell title="HSE / Calidad" description="Inspecciones, no conformidades y comisionado de la obra.">
    {(summary) => <QualityContent summary={summary} />}
  </ConstructionShell>
);

export default ConstructionQuality;
